import { lazy, type ComponentType } from "react";

/**
 * Every deploy replaces the previous build's hashed chunk files. A tab that
 * was open before the deploy still runs the old build, so its next lazy page
 * load asks for a chunk that no longer exists. That is not an app error: the
 * current index.html fixes it. The page reloads once, staying in its loading
 * state meanwhile, so the error never reaches the error boundary or Sentry.
 * If the same failure comes back within RELOAD_WINDOW_MS (a chunk genuinely
 * missing from the current build), it is thrown as a real error instead of
 * reloading again.
 */
const CHUNK_LOAD_ERROR =
  /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|unable to preload css/i;
const RELOAD_KEY = "rekinder:chunk-reload-at";
export const RELOAD_WINDOW_MS = 30_000;

export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return CHUNK_LOAD_ERROR.test(message);
}

type Deps = {
  now?: () => number;
  storage?: Pick<Storage, "getItem" | "setItem"> | null;
  reload?: () => void;
};

function sessionStore(): Pick<Storage, "getItem" | "setItem"> | null {
  try {
    return typeof sessionStorage === "undefined" ? null : sessionStorage;
  } catch {
    return null;
  }
}

/** Reloads the page unless it was already reloaded for this within the window. */
export function reloadOnceForStaleChunk({
  now = Date.now,
  storage = sessionStore(),
  reload = () => window.location.reload(),
}: Deps = {}): boolean {
  // Without storage there is no way to tell a second failure from the first.
  if (!storage) return false;
  try {
    const last = Number(storage.getItem(RELOAD_KEY) || 0);
    if (now() - last < RELOAD_WINDOW_MS) return false;
    storage.setItem(RELOAD_KEY, String(now()));
  } catch {
    return false;
  }
  reload();
  return true;
}

/** The module, or — for a stale chunk that triggers a reload — a promise that never settles. */
export function loadWithStaleChunkReload<T>(factory: () => Promise<T>, deps?: Deps): Promise<T> {
  return factory().catch((error: unknown) => {
    if (isChunkLoadError(error) && reloadOnceForStaleChunk(deps)) return new Promise<T>(() => {});
    throw error;
  });
}

/** React.lazy for route pages, recovering from a stale chunk after a deploy. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyPage<T extends ComponentType<any>>(factory: () => Promise<{ default: T }>) {
  return lazy(() => loadWithStaleChunkReload(factory));
}
